import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /**
   * Bumped whenever the cart changes. A product page fetched before a cart change describes the
   * old cart (the upsell line counts it), so a fetch still in flight when the cart changes must
   * not be cached when it lands: clearing the cache alone let a prefetch put the stale page back.
   */
  #cacheGeneration = 0;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // Givino: fetch the picker's content on the first sign of intent on the button itself, so it
    // is usually cached by the time the tap lands. Only the button: a touch on the card while a
    // rail is being swiped is not intent to buy.
    this.addEventListener('pointerenter', this.#prefetch, { once: true });
    this.addEventListener('touchstart', this.#prefetch, { once: true, passive: true });
    this.addEventListener('focusin', this.#prefetch, { once: true });
  }

  #prefetch = () => {
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.has(url) || this.dataset.quickAddButton !== 'choose') return;
    this.#loadContent(url).catch(() => {});
  };

  /**
   * The product page's grid content for a URL, fetched once and cached until the cart changes.
   * @param {string} url
   * @returns {Promise<Element | undefined>}
   */
  async #loadContent(url) {
    let productGrid = this.#cachedContent.get(url);
    if (productGrid) return productGrid;
    const generation = this.#cacheGeneration;
    const html = await this.fetchProductPage(url);
    const gridElement = html?.querySelector('[data-product-grid-content]');
    if (gridElement) {
      productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      // Served to whoever asked, but kept only if the cart has not changed since the fetch began.
      if (generation === this.#cacheGeneration) this.#cachedContent.set(url, productGrid);
    }
    return productGrid;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when the cart changes. Twice: when the change is announced, and again
   * when it settles, because the event fires as the request goes out, so a page fetched in between
   * can still be rendered from the cart before the change. gate:quick-add-cache holds it.
   * @param {CartLinesUpdateEvent} [event]
   */
  #handleCartUpdate = (event) => {
    const invalidate = () => {
      this.#cacheGeneration += 1;
      this.#cachedContent.clear();
    };
    invalidate();
    event?.promise?.then(invalidate, invalidate);
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Givino: products with plans no longer go to their page; the picker offers the plans itself.
    const productGrid = await this.#loadContent(currentUrl);

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    // The picker's title names the dialog; Horizon's own layout has no such title, so the
    // reference is set only when it can resolve.
    const dialogElement = dialogComponent.refs?.dialog;
    if (dialogComponent.querySelector('#QuickAddPickerTitle')) {
      dialogElement?.setAttribute('aria-labelledby', 'QuickAddPickerTitle');
    } else {
      dialogElement?.removeAttribute('aria-labelledby');
    }

    dialogComponent.showDialog();

    // Givino picker: start on the choice that matters, the ticked quantity, rather than the close
    // button. The dialog restores focus to this card's button when it closes.
    requestAnimationFrame(() => {
      const start = dialogComponent.querySelector('.gv-picker case-ladder input:checked, .gv-picker input[type="radio"]:checked');
      if (start instanceof HTMLElement) start.focus({ preventScroll: true });
    });

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (this.#buildPicker(productGrid)) {
      // Givino picker built; Horizon's own layouts below are the fallback when the page lacks the
      // pieces it needs.
    } else if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Givino quick add picker (PLAN-quickadd-picker.md). Rebuilds the fetched product page as a lean
   * picker, the same at every breakpoint (a bottom sheet on phones, a compact dialog from 750px, by
   * CSS): the bottle, title (the link to the full page), price and stock; then the product page's
   * own case ladder, buy once or subscribe, and club line; then the upsell line and the add form.
   * These are the product page's blocks, moved rather than copied, so the picker can never offer
   * something the product page does not. Everything else on the page (gallery, notes, gift box,
   * delivery) is left out.
   * @param {Element} productGrid - The fetched product grid content (a clone, safe to rearrange)
   * @returns {boolean} whether the picker was built
   */
  #buildPicker(productGrid) {
    const form = productGrid.querySelector('product-form-component');
    if (!form) return false;
    const find = (/** @type {string} */ selector) => productGrid.querySelector(selector);

    const picker = document.createElement('div');
    picker.className = 'gv-picker';

    const head = document.createElement('div');
    head.className = 'gv-picker__head';

    const thumb = document.createElement('span');
    thumb.className = 'gv-picker__thumb';
    const cardImage = this.closest('product-card')?.querySelector('img');
    const image = /** @type {HTMLImageElement | null} */ ((cardImage ?? find('.product-information__media img'))?.cloneNode(true) ?? null);
    if (image) {
      image.removeAttribute('fetchpriority');
      image.loading = 'eager';
      image.alt = '';
      image.sizes = '72px';
      thumb.append(image);
    }
    head.append(thumb);

    const id = document.createElement('div');
    id.className = 'gv-picker__id';
    const title = document.createElement('h2');
    title.className = 'gv-picker__title';
    title.id = 'QuickAddPickerTitle';
    const link = document.createElement('a');
    link.href = this.productPageUrl;
    link.textContent = this.dataset.productTitle || '';
    title.append(link);
    id.append(title);
    for (const selector of ['product-price', 'product-inventory']) {
      const el = find(selector);
      if (el) id.append(el);
    }
    head.append(id);

    const body = document.createElement('div');
    body.className = 'gv-picker__body';
    for (const selector of ['variant-picker', 'case-ladder', 'purchase-options', '.club-card']) {
      const el = find(selector);
      if (el) body.append(el);
    }

    const foot = document.createElement('div');
    foot.className = 'gv-picker__foot';
    const nudge = find('upsell-nudge');
    if (nudge) foot.append(nudge);
    foot.append(form);

    picker.append(head, body, foot);
    this.#giveFormItsOwnId(picker);
    productGrid.replaceChildren(picker);
    return true;
  }

  /**
   * The moved form gets an id of its own, and every control joined to it by the `form` attribute
   * (the plan pills, blocks/purchase-options.liquid) follows. On a product page the fetched product
   * comes from the same section as the page's, so its form carries the page form's id, and a `form`
   * attribute resolves to the FIRST element with that id: the picker's pills would join the page's
   * form, post nothing from the picker, and share one radio group with the page's own pills.
   * Read and set through getAttribute: a product form holds an input named `id`, so `form.id` is
   * that input, not the form's id.
   * @param {Element} picker
   */
  #giveFormItsOwnId(picker) {
    const htmlForm = picker.querySelector('product-form-component form');
    const oldId = htmlForm?.getAttribute('id');
    if (!htmlForm || !oldId) return;
    const newId = `QuickAdd-${oldId}`;
    htmlForm.setAttribute('id', newId);
    for (const control of picker.querySelectorAll('[form]')) {
      if (control.getAttribute('form') === oldId) control.setAttribute('form', newId);
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
        this.#showCart();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /**
   * Givino (PLAN-quickadd-picker.md Q10): after an add from the picker, the side cart opens, so the
   * shopper sees the case filling. The drawer already does this itself when set to open on add; this
   * covers the setting being off (it is, for the product page's own adds), and waits for the dialog
   * to finish closing so focus is restored before the drawer takes it.
   */
  #showCart() {
    const cart = document.querySelector('cart-drawer-component');
    if (!cart || cart.hasAttribute('auto-open')) return;
    const drawer = /** @type {{ open?: () => void } | null} */ (cart.closest('theme-drawer'));
    const dialog = this.refs?.dialog;
    const open = () => drawer?.open?.();
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      dialog.addEventListener('close', open, { once: true });
    } else {
      open();
    }
  }

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
